import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
    ForwardedRef,
} from "react";
import { Card, Checkbox, Input } from "../../../../../shared/custom-ui";

interface UltrasoundFormProps {
    defaultValues?: Record<string, any>;
}

const UltrasoundForm = forwardRef((props: UltrasoundFormProps, ref: ForwardedRef<any>) => {
    const { defaultValues = {} } = props;
    const [ultrasoundObj, setUltrasoundObj] = useState<Record<string, any>>({});
    const [isPrefilled, setIsPrefilled] = useState(false);

    // ✅ Only apply default values once
    useEffect(() => {
        if (!isPrefilled && Object.keys(defaultValues).length > 0) {
            setUltrasoundObj(prev => ({ ...prev, ...defaultValues }));
            setIsPrefilled(true);
        }
    }, [defaultValues, isPrefilled]);

    useImperativeHandle(ref, () => ({
        getData: () => ultrasoundObj,
    }));

    const handleCheckboxChange = (key: string) => {
        setUltrasoundObj(prev => {
            if (prev[key]) {
                const { [key]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [key]: true };
        });
    };

    const handleInputChange = (key: string, value: string | null) => {
        setUltrasoundObj(prev => {
            if (value) {
                return { ...prev, [key]: value };
            } else {
                const { [key]: _, ...rest } = prev;
                return rest;
            }
        });
    };

    return (
        <Card title="ULTRASOUND (U/S)" padding="small">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                <Checkbox
                    label="Aorta"
                    checked={ultrasoundObj.aorta || false}
                    onChange={() => handleCheckboxChange("aorta")}
                />
                <Checkbox
                    label="Abdominal Aortic Aneurysm (AAA) Screening (see payer guidelines)"
                    checked={ultrasoundObj.aaa_screening || false}
                    onChange={() => handleCheckboxChange("aaa_screening")}
                />
                <Checkbox
                    label="Abdomen with Liver Elastography"
                    checked={ultrasoundObj.abdomen_liver_elastography || false}
                    onChange={() => handleCheckboxChange("abdomen_liver_elastography")}
                />
                <Checkbox
                    label="Abdomen (complete)"
                    checked={ultrasoundObj.abdomen_complete || false}
                    onChange={() => handleCheckboxChange("abdomen_complete")}
                />
                <Checkbox
                    label="Carotid doppler"
                    checked={ultrasoundObj.carotid_doppler || false}
                    onChange={() => handleCheckboxChange("carotid_doppler")}
                />
                <Checkbox
                    label="Gallbladder/RUQ"
                    checked={ultrasoundObj.gallbladder_ruq || false}
                    onChange={() => handleCheckboxChange("gallbladder_ruq")}
                />
                <Checkbox
                    label="Shoulder"
                    checked={ultrasoundObj.shoulder || false}
                    onChange={() => handleCheckboxChange("shoulder")}
                />
                <Checkbox
                    label="1st Trimester OB ultrasound with endovaginal if medically indicated"
                    checked={ultrasoundObj.ob_ultrasound || false}
                    onChange={() => handleCheckboxChange("ob_ultrasound")}
                />
                <div className="flex items-center space-x-4">
                    <Checkbox
                        label="Pelvis"
                        checked={ultrasoundObj.pelvis || false}
                        onChange={() => handleCheckboxChange("pelvis")}
                    />
                    <Checkbox
                        label="with endovaginal if medically indicated"
                        checked={ultrasoundObj.pelvis_endovaginal || false}
                        onChange={() => handleCheckboxChange("pelvis_endovaginal")}
                    />
                </div>
                <Checkbox
                    label="Endovaginal only"
                    checked={ultrasoundObj.endovaginal_only || false}
                    onChange={() => handleCheckboxChange("endovaginal_only")}
                />
                <div className="flex items-center space-x-4">
                    <Checkbox
                        label="Groin"
                        checked={ultrasoundObj.groin || false}
                        onChange={() => handleCheckboxChange("groin")}
                    />
                    <div className="flex items-center space-x-4">
                        <Checkbox
                            label="L"
                            checked={ultrasoundObj.groin_left || false}
                            onChange={() => handleCheckboxChange("groin_left")}
                        />
                        <Checkbox
                            label="R"
                            checked={ultrasoundObj.groin_right || false}
                            onChange={() => handleCheckboxChange("groin_right")}
                        />
                    </div>
                </div>
                <Checkbox
                    label="Scrotum/testicular"
                    checked={ultrasoundObj.scrotum_testicular || false}
                    onChange={() => handleCheckboxChange("scrotum_testicular")}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 md:items-end gap-4">
                    <Checkbox
                        label="Soft tissue mass (specify area or extremity)"
                        checked={ultrasoundObj.soft_tissue_mass || false}
                        onChange={() => handleCheckboxChange("soft_tissue_mass")}
                    />
                    <Input
                        placeholder="Specify area or extremity"
                        value={ultrasoundObj.soft_tissue_mass_specify || ""}
                        onChange={(e) => handleInputChange("soft_tissue_mass_specify", e.target.value)}
                    />
                </div>
                <div className="flex items-center space-x-4">
                    <Checkbox
                        label="Thyroid"
                        checked={ultrasoundObj.thyroid || false}
                        onChange={() => handleCheckboxChange("thyroid")}
                    />
                    <Checkbox
                        label="Thyroid biopsy if medically indicated"
                        checked={ultrasoundObj.thyroid_biopsy || false}
                        onChange={() => handleCheckboxChange("thyroid_biopsy")}
                    />
                </div>
                <div className="flex items-center space-x-4">
                    <Checkbox
                        label="Urinary tract (kidneys & bladder)"
                        checked={ultrasoundObj.urinary_tract || false}
                        onChange={() => handleCheckboxChange("urinary_tract")}
                    />
                    <Checkbox
                        label="Bladder only"
                        checked={ultrasoundObj.bladder_only || false}
                        onChange={() => handleCheckboxChange("bladder_only")}
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Checkbox
                        label="Venous doppler, lower extremity (DVT)"
                        checked={ultrasoundObj.venous_doppler_lower_extremity || false}
                        onChange={() => handleCheckboxChange("venous_doppler_lower_extremity")}
                    />
                    <div className="flex items-center space-x-4">
                        <Checkbox
                            label="L"
                            checked={ultrasoundObj.venous_doppler_lower_extremity_left || false}
                            onChange={() => handleCheckboxChange("venous_doppler_lower_extremity_left")}
                        />
                        <Checkbox
                            label="R"
                            checked={ultrasoundObj.venous_doppler_lower_extremity_right || false}
                            onChange={() => handleCheckboxChange("venous_doppler_lower_extremity_right")}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Checkbox
                        label="Venous doppler, upper extremity"
                        checked={ultrasoundObj.venous_doppler_upper_extremity || false}
                        onChange={() => handleCheckboxChange("venous_doppler_upper_extremity")}
                    />
                    <div className="flex items-center space-x-4">
                        <Checkbox
                            label="L"
                            checked={ultrasoundObj.venous_doppler_upper_extremity_left || false}
                            onChange={() => handleCheckboxChange("venous_doppler_upper_extremity_left")}
                        />
                        <Checkbox
                            label="R"
                            checked={ultrasoundObj.venous_doppler_upper_extremity_right || false}
                            onChange={() => handleCheckboxChange("venous_doppler_upper_extremity_right")}
                        />
                    </div>
                </div>
                <Checkbox
                    label="Venous insufficiency (varicose vein evaluation)"
                    checked={ultrasoundObj.venous_insufficiency || false}
                    onChange={() => handleCheckboxChange("venous_insufficiency")}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Checkbox
                        label="Other"
                        checked={ultrasoundObj.other || false}
                        onChange={() => handleCheckboxChange("other")}
                    />
                    <Input
                        placeholder="Specify other"
                        value={ultrasoundObj.specify_other || ""}
                        onChange={(e) => handleInputChange("specify_other", e.target.value)}
                    />
                </div>
            </div>
        </Card>
    );
});

export default UltrasoundForm;
